/**
 * Inline AI citation, as the ICS-202 run writes it: `<span
 * class="citation-placeholder" data-cite="1" data-file-id="…" data-page="4">[1,
 * p. 4]</span>`. An atom, so editing text around it never splits or retypes the
 * label.
 */
import { Node, mergeAttributes } from '@tiptap/core';

const dataAttr = (name: string) => ({
  default: null,
  parseHTML: (element: HTMLElement) => element.getAttribute(`data-${name}`),
  renderHTML: (attributes: Record<string, unknown>) => {
    const key = name.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
    const value = attributes[key];
    return value == null || value === '' ? {} : { [`data-${name}`]: value };
  },
});

export const CitationChip = Node.create({
  name: 'citationChip',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      cite: dataAttr('cite'),
      sourceType: dataAttr('source-type'),
      sourceName: dataAttr('source-name'),
      fileId: dataAttr('file-id'),
      page: dataAttr('page'),
      url: dataAttr('url'),
      label: {
        default: '',
        parseHTML: (element: HTMLElement) => element.textContent ?? '',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-cite]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const label =
      (node.attrs.label as string) || `[${node.attrs.cite as string}]`;
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'citation-placeholder',
        title: [
          node.attrs.sourceName,
          node.attrs.page ? `page ${node.attrs.page}` : '',
        ]
          .filter(Boolean)
          .join(', '),
      }),
      label,
    ];
  },

  renderText({ node }) {
    return (node.attrs.label as string) || `[${node.attrs.cite as string}]`;
  },
});
